import { useState } from 'react'
import styles from './controls.module.css'
import CountPill from './CountPill'
import type { FilterOption } from 'lib/dealhunt/types'

interface SearchControlProps {
  options: FilterOption[]
  /** Pills shown before "Show all N" (selected options are always pinned on top of these). */
  top: number
  placeholder: string
  /** A dist group's tail aggregate — always offered as the trailing pill. */
  other?: FilterOption | null
  /** The backend's sentence under the control (dist groups). */
  sentence?: string | null
  onOption: (option: FilterOption, on: boolean) => void
}

/** What the pill row shows: every match while typing or expanded, else the top N
 *  plus any selected option beyond it (a pick never scrolls out of sight). */
export function visibleOptions(
  options: FilterOption[],
  top: number,
  query: string,
  expanded: boolean
): { visible: FilterOption[]; hidden: number } {
  const q = query.trim().toLowerCase()
  const matching = q ? options.filter(o => o.label.toLowerCase().includes(q)) : options
  const visible =
    q || expanded
      ? matching
      : [...matching.slice(0, top), ...matching.slice(top).filter(o => o.selected)]
  return { visible, hidden: matching.length - visible.length }
}

/** Typeahead over a long option list (Brand, or a `dist` group past
 *  DIST_TYPEAHEAD_MIN options): top-N pills, filter-as-you-type, selected options
 *  always pinned visible. Group-agnostic — the caller names the noun via
 *  `placeholder`; option intent goes back through `onOption` unchanged. */
const SearchControl: React.VFC<SearchControlProps> = ({
  options,
  top,
  placeholder,
  other = null,
  sentence = null,
  onOption,
}) => {
  const [query, setQuery] = useState('')
  const [expanded, setExpanded] = useState(false)
  const q = query.trim().toLowerCase()
  const { visible, hidden } = visibleOptions(options, top, query, expanded)
  const showOther =
    other !== null && (!q || other.selected || other.label.toLowerCase().includes(q))

  return (
    <>
      <input
        type="search"
        className={styles.search}
        placeholder={placeholder}
        value={query}
        aria-label={placeholder}
        onChange={e => setQuery(e.target.value)}
      />
      <div className={styles.pills}>
        {visible.map(opt => (
          <CountPill
            key={opt.value}
            label={opt.label}
            count={opt.count}
            selected={opt.selected}
            onClick={() => onOption(opt, !opt.selected)}
          />
        ))}
        {showOther && other && (
          <CountPill
            key={`other:${other.value}`}
            label={other.label}
            count={other.count}
            selected={other.selected}
            onClick={() => onOption(other, !other.selected)}
          />
        )}
        {!q && hidden > 0 && (
          <button type="button" className={styles.showMore} onClick={() => setExpanded(true)}>
            {`Show all ${options.length}`}
          </button>
        )}
        {!q && expanded && options.length > top && (
          <button type="button" className={styles.showMore} onClick={() => setExpanded(false)}>
            {'Show fewer'}
          </button>
        )}
      </div>
      {sentence && <p className={styles.sentence}>{sentence}</p>}
    </>
  )
}

export default SearchControl
